/**
 * Umschaltung zwischen den Ansichten.
 *
 * Die eigentliche Gestaltung liegt in `src/styles/themes.css`; hier steht
 * nur, welches Thema gilt, und wie es gespeichert wird.
 *
 * Der Dunkelmodus ist davon unabhängig und bleibt in `AuthContext`.
 */

export const THEMEN = ['aventurien', 'klar'] as const;
export type Thema = (typeof THEMEN)[number];

export const STANDARD_THEMA: Thema = 'aventurien';

/** Anzeigenamen für die Umschaltfläche. */
export const THEMA_BESCHRIFTUNG: Record<Thema, string> = {
  aventurien: 'Aventurien',
  klar: 'Klar',
};

export const THEMA_BESCHREIBUNG: Record<Thema, string> = {
  aventurien: 'Gewohnte Optik mit Pergament und Zierschrift',
  klar: 'Neutrale, ruhige Ansicht ohne Hintergrundbild',
};

const SPEICHERSCHLUESSEL = 'thema';

/** Prüft, ob ein beliebiger Wert ein bekanntes Thema ist. */
export const istThema = (wert: unknown): wert is Thema =>
  typeof wert === 'string' && (THEMEN as readonly string[]).includes(wert);

/**
 * Liest das gespeicherte Thema.
 *
 * Fällt auf die Vorgabe zurück, wenn nichts oder Unbekanntes gespeichert ist –
 * und auch dann, wenn der Zugriff auf den Speicher scheitert, etwa im
 * privaten Fenster.
 */
export const leseThema = (): Thema => {
  try {
    const gespeichert = localStorage.getItem(SPEICHERSCHLUESSEL);
    return istThema(gespeichert) ? gespeichert : STANDARD_THEMA;
  } catch {
    return STANDARD_THEMA;
  }
};

/** Schreibt das Thema in das Dokument und merkt es sich. */
export const setzeThema = (thema: Thema): void => {
  const gueltig = istThema(thema) ? thema : STANDARD_THEMA;

  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('data-theme', gueltig);
  }

  try {
    localStorage.setItem(SPEICHERSCHLUESSEL, gueltig);
  } catch {
    // Ohne Speicher gilt das Thema nur für diese Sitzung. Kein Grund,
    // die Umschaltung deshalb scheitern zu lassen.
  }
};

/** Das jeweils andere Thema – für eine einfache Umschaltfläche. */
export const naechstesThema = (aktuell: Thema): Thema =>
  aktuell === 'aventurien' ? 'klar' : 'aventurien';
